import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Mail, User, Building, MapPin, ChevronDown, X, Check, Sparkles, Globe, Shield, Database, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROTATING = ['Kenya', 'Indonesia', 'Brazil', 'Vietnam', 'Mexico', 'Morocco', 'Philippines', 'Nigeria'];

function RotatingCountry() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(x => (x + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-block min-w-[180px]">
      <span key={i} className="inline-block animate-fadeInUp bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">
        {ROTATING[i]}
      </span>
    </span>
  );
}

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [areasOfInterest, setAreasOfInterest] = useState([]);
  const [sectorSearch, setSectorSearch] = useState('');
  const [showSectors, setShowSectors] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sectors`).then(res => setSectors(res.data.sectors || [])).catch(() => {});
  }, []);

  const filteredSectors = sectors.filter(s => s.toLowerCase().includes(sectorSearch.toLowerCase()));

  const toggleSector = (s) => {
    setAreasOfInterest(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        toast.success('Login successful!');
        onLogin(res.data.token);
      } else {
        await axios.post(`${API}/auth/register`, {
          email, password, full_name: fullName,
          company, location, areas_of_interest: areasOfInterest
        });
        toast.success('Registration submitted! Your account is pending administrator approval.');
        setIsLogin(true);
        setEmail('');
        setPassword('');
        setFullName('');
        setCompany('');
        setLocation('');
        setAreasOfInterest([]);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Authentication failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9ff] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.45s ease-out forwards; }
        @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpin { animation: slowSpin 40s linear infinite; }
        .grid-bg { background-image: linear-gradient(rgba(0,74,198,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.06) 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>

      {/* Left — Kinetic hero */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-[#0b1c30] via-[#1a2d4a] to-[#004ac6] text-white p-10 flex-col justify-between">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-[#7c3aed]/20 to-transparent animate-slowSpin pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')} data-testid="login-brand-link">
          <img src="/logo-small.png" alt="Sovereign IQ" className="w-10 h-10 rounded-lg object-contain bg-white/5 p-1" />
          <div>
            <h1 className="text-base font-bold tracking-tighter">Sovereign IQ</h1>
            <p className="text-[9px] text-white/60 uppercase tracking-widest">Intelligence Platform</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-6 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/80">Live intelligence · real-time</span>
          </div>
          <h2 className="text-4xl font-extrabold tracking-tighter leading-[1.05] mb-4">
            Your next $50M move into
            <br />
            <RotatingCountry />
            <br />
            deserves conviction.
          </h2>
          <p className="text-sm text-white/70 leading-relaxed mb-8">
            31-section, investment-grade country blueprints generated in under 90 minutes — powered by our AI intelligence engine and 1,000+ real-time data sources.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Globe, label: '180+ countries' },
              { icon: Database, label: '1,000+ sources' },
              { icon: TrendingUp, label: '90 min to ship' },
              { icon: Shield, label: 'Multi-tenant' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-white/80">
                <f.icon className="w-4 h-4 text-[#a78bfa] shrink-0" />
                <span className="font-semibold tracking-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[10px] text-white/40 uppercase tracking-widest">
          Trusted by PE funds, DFIs, and sovereign investors
        </div>
      </div>

      {/* Right — Auth form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#f8f9ff] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="w-full max-w-md relative z-10">
          <div className="flex items-center gap-3 mb-6 justify-center cursor-pointer lg:hidden" onClick={() => navigate('/')}>
            <img src="/logo-small.png" alt="Sovereign IQ" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-lg font-bold tracking-tighter text-[#0b1c30]">Sovereign IQ</h1>
              <p className="text-[9px] text-[#434655] uppercase tracking-widest">Intelligence Platform</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-8 border border-[#c3c6d7]/30">
            <div className="inline-flex items-center gap-2 bg-[#eff4ff] border border-[#004ac6]/20 rounded-full px-2.5 py-0.5 mb-3">
              <Sparkles className="w-3 h-3 text-[#004ac6]" />
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#004ac6]">
                {isLogin ? 'Welcome back' : 'Request access'}
              </span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tighter text-[#0b1c30] mb-1">
              {isLogin ? 'Sign in to the platform' : 'Create your account'}
            </h2>
            <p className="text-xs text-[#434655] mb-6">
              {isLogin ? 'Enter your credentials to access your blueprints.' : 'Register for access. Admin approval required.'}
            </p>

          <div className="flex mb-6 bg-[#f8f9ff] rounded-lg p-1">
            <button onClick={() => setIsLogin(true)} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${isLogin ? 'bg-[#004ac6] text-white shadow-sm' : 'text-[#434655]'}`} data-testid="login-tab">Sign In</button>
            <button onClick={() => setIsLogin(false)} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${!isLogin ? 'bg-[#004ac6] text-white shadow-sm' : 'text-[#434655]'}`} data-testid="register-tab">Register</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {!isLogin && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required
                      className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="Your full name" data-testid="register-name-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Company</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                    <input type="text" value={company} onChange={e => setCompany(e.target.value)}
                      className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="Company name" data-testid="register-company-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                    <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                      className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="City, Country" data-testid="register-location-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Areas of Interest</label>
                  {areasOfInterest.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {areasOfInterest.map(s => (
                        <span key={s} className="text-[10px] font-semibold bg-[#e5eeff] text-[#004ac6] px-2 py-0.5 rounded-full flex items-center gap-1">
                          {s.length > 25 ? s.slice(0, 25) + '...' : s}
                          <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => toggleSector(s)} />
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input type="text" value={sectorSearch} onChange={e => { setSectorSearch(e.target.value); setShowSectors(true); }}
                      onFocus={() => setShowSectors(true)}
                      className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="Search sectors..." data-testid="register-sectors-search" />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                    {showSectors && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-[#c3c6d7]/40 max-h-48 overflow-y-auto">
                        {filteredSectors.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-[#434655]">No sectors found</div>
                        ) : filteredSectors.slice(0, 30).map(s => (
                          <div key={s} onClick={() => { toggleSector(s); setSectorSearch(''); }} data-testid={`sector-option-${s.slice(0,20)}`}
                            className={`px-3 py-2 text-xs cursor-pointer flex items-center justify-between hover:bg-[#f8f9ff] ${areasOfInterest.includes(s) ? 'bg-[#e5eeff] text-[#004ac6] font-semibold' : 'text-[#434655]'}`}>
                            {s}
                            {areasOfInterest.includes(s) && <Check className="w-3 h-3 text-[#004ac6]" />}
                          </div>
                        ))}
                        <button type="button" onClick={() => setShowSectors(false)} className="w-full text-center py-1.5 text-[10px] text-[#434655] border-t border-[#c3c6d7]/20 hover:bg-[#f8f9ff]">Close</button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="analyst@company.com" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#434655] font-semibold mb-1 block">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c3c6d7]" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg pl-10 pr-4 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none" placeholder="Secure password" data-testid="login-password-input" />
              </div>
              {isLogin && (
                <div className="text-right mt-1.5">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    data-testid="forgot-password-link"
                    className="text-[11px] font-semibold text-[#004ac6] hover:text-[#003a9e] hover:underline">
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-[#004ac6] to-[#7c3aed] text-white py-3 rounded-lg text-sm font-bold hover:shadow-lg transition-all disabled:opacity-50 mt-2 shadow-md"
              data-testid="login-submit-btn">
              {loading ? 'Processing...' : isLogin ? 'Access Platform' : 'Submit Registration'}
            </button>
          </form>
        </div>

        <p className="text-center text-[10px] text-[#737686] mt-6">
          {!isLogin ? 'New accounts require administrator approval.' : 'Secure connection — Confidential'}
        </p>
      </div>
      </div>
    </div>
  );
}

export default LoginPage;
